// src/components/AccountOffsetDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Alert,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Paper,
  Grid,
  Chip
} from '@mui/material';
import {
  Warning,
  CheckCircle,
  Info,
  MoneyOff,
  TrendingDown,
  AccountBalance
} from '@mui/icons-material';
import { previewAccountOffset, executeAccountOffset } from './api';

const AccountOffsetDialog = ({ open, onClose, memberDetails, onOffsetComplete }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  
  // Form state
  const [offsetLoans, setOffsetLoans] = useState(true);
  const [offsetAdvances, setOffsetAdvances] = useState(true);
  const [includeProcessingFee, setIncludeProcessingFee] = useState(false);
  const [useExcessAsFees, setUseExcessAsFees] = useState(false);
  const [notes, setNotes] = useState('');

  // Load preview when dialog opens
  useEffect(() => {
    if (open && memberDetails) {
      loadPreview();
    }
  }, [open, memberDetails]);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const previewData = await previewAccountOffset(memberDetails.id, {
        offset_loans: offsetLoans,
        offset_advances: offsetAdvances,
        confirmed: false
      });
      
      setPreview(previewData);
      
      // Set checkbox defaults based on preview
      if (previewData.processing_fee_options?.include_processing_fee_checkbox) {
        setIncludeProcessingFee(
          previewData.processing_fee_options.include_processing_fee_checkbox.checked_by_default
        );
      }
      
      if (previewData.processing_fee_options?.use_excess_as_fees_checkbox?.visible) {
        setUseExcessAsFees(
          previewData.processing_fee_options.use_excess_as_fees_checkbox.checked_by_default
        );
      }
      
    } catch (err) {
      console.error('Error loading preview:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteOffset = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await executeAccountOffset(memberDetails.id, {
        offset_loans: offsetLoans,
        offset_advances: offsetAdvances,
        confirmed: true,
        include_processing_fee: includeProcessingFee,
        use_excess_as_fees: useExcessAsFees,
        officer_name: 'System', // You can get this from auth context
        notes: notes.trim()
      });
      
      if (result.success) {
        // Show success message
        alert(`Offset completed successfully!\n\nDetails:\n- Savings used: KES ${result.offset_details.total_savings_used.toLocaleString()}\n- Loan offset: KES ${result.offset_details.loan_offset.toLocaleString()}\n- Advance offset: KES ${result.offset_details.advance_offset.toLocaleString()}\n- Total fees: KES ${result.offset_details.total_fees_charged.toLocaleString()}`);
        
        // Callback to parent to refresh data
        if (onOffsetComplete) {
          onOffsetComplete(result);
        }
        
        onClose();
      }
    } catch (err) {
      console.error('Error executing offset:', err);
      setError(err.response?.data?.message || err.message || 'Failed to execute offset');
    } finally {
      setLoading(false);
    }
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!preview) return null;

    const feeOptions = preview.processing_fee_options;
    const currentBalances = preview.current_balances;
    const offsetPlan = preview.offset_plan;

    // Determine which scenario to show based on execution_scenarios
    const scenarios = preview.execution_scenarios;
    let selectedScenario = null;
    
    if (scenarios) {
      if (includeProcessingFee && useExcessAsFees && scenarios.both_checked) {
        selectedScenario = scenarios.both_checked;
      } else if (includeProcessingFee && scenarios.only_fee_checked) {
        selectedScenario = scenarios.only_fee_checked;
      } else if (includeProcessingFee && scenarios.fee_checked) {
        selectedScenario = scenarios.fee_checked;
      } else if (!includeProcessingFee && scenarios.neither_checked) {
        selectedScenario = scenarios.neither_checked;
      } else if (!includeProcessingFee && scenarios.fee_unchecked) {
        selectedScenario = scenarios.fee_unchecked;
      }
    }

    return (
      <>
        {/* Current Balances - FIXED STYLING */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 2.5, 
            mb: 2, 
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="subtitle2" gutterBottom sx={{ mb: 2, color: 'text.primary' }}>
            Current Balances
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 1, 
                bgcolor: 'success.lighter',
                border: '1px solid',
                borderColor: 'success.light'
              }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Savings
                </Typography>
                <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600 }}>
                  KES {currentBalances.savings_balance.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 1, 
                bgcolor: 'error.lighter',
                border: '1px solid',
                borderColor: 'error.light'
              }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Loans
                </Typography>
                <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 600 }}>
                  KES {currentBalances.loan_balance.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 1, 
                bgcolor: 'warning.lighter',
                border: '1px solid',
                borderColor: 'warning.light'
              }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Advances
                </Typography>
                <Typography variant="h6" sx={{ color: 'warning.main', fontWeight: 600 }}>
                  KES {currentBalances.advance_balance.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Total Debt:
            </Typography>
            <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 600 }}>
              KES {currentBalances.total_debt.toLocaleString()}
            </Typography>
          </Box>
        </Paper>

        {/* Offset Plan - FIXED STYLING */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 2.5, 
            mb: 2, 
            bgcolor: 'background.paper',
            border: '2px solid',
            borderColor: 'primary.main'
          }}
        >
          <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
            Offset Plan
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Loan Offset:
              </Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ color: 'text.primary' }}>
                KES {offsetPlan.loan_offset.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Advance Offset:
              </Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ color: 'text.primary' }}>
                KES {offsetPlan.advance_offset.toLocaleString()}
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" fontWeight="medium" sx={{ color: 'text.primary' }}>
                Savings After Debt Offset:
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="success.main">
                KES {offsetPlan.savings_after_debt_offset.toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Processing Fee Options - FIXED STYLING */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 2.5, 
            mb: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.primary', mb: 2 }}>
            Processing Fee Options (KES {feeOptions.standard_fee.toLocaleString()})
          </Typography>

          {/* Warning if applicable */}
          {feeOptions.warning && (
            <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
              {feeOptions.warning}
            </Alert>
          )}

          {/* Info message if applicable */}
          {feeOptions.info_message && (
            <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
              {feeOptions.info_message}
            </Alert>
          )}

          {/* Include Processing Fee Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={includeProcessingFee}
                onChange={(e) => setIncludeProcessingFee(e.target.checked)}
                disabled={
                  !feeOptions.include_processing_fee_checkbox?.can_deduct_from_savings
                }
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  Include processing fee from savings
                </Typography>
                {feeOptions.include_processing_fee_checkbox?.amount_available_for_fee > 0 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {feeOptions.include_processing_fee_checkbox.full_fee_covered
                      ? `Full fee of KES ${feeOptions.standard_fee} can be covered`
                      : `Only KES ${feeOptions.include_processing_fee_checkbox.partial_amount} available for partial fee`}
                  </Typography>
                )}
              </Box>
            }
          />

          {/* Use Excess as Fees Checkbox (only visible when there's excess) */}
          {feeOptions.use_excess_as_fees_checkbox?.visible && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={useExcessAsFees}
                  onChange={(e) => setUseExcessAsFees(e.target.checked)}
                  disabled={!includeProcessingFee}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {feeOptions.use_excess_as_fees_checkbox.description}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Additional KES {feeOptions.use_excess_as_fees_checkbox.excess_amount.toLocaleString()} will be treated as offset fees
                  </Typography>
                </Box>
              }
            />
          )}
        </Paper>

        {/* Selected Scenario Preview - FIXED STYLING */}
        {selectedScenario && (
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              mb: 2, 
              bgcolor: 'success.lighter',
              border: '1px solid',
              borderColor: 'success.light'
            }}
          >
            <Typography variant="subtitle2" gutterBottom sx={{ color: 'success.dark', mb: 1.5 }}>
              <CheckCircle sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
              Selected Outcome
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.primary' }}>
              {selectedScenario.description}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1,
              p: 1.5,
              bgcolor: 'background.paper',
              borderRadius: 1
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total Savings Used:
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ color: 'text.primary' }}>
                  KES {selectedScenario.total_savings_used.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total Fees Charged:
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ color: 'text.primary' }}>
                  KES {selectedScenario.total_fees_charged.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Remaining Savings:
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="success.main">
                  KES {selectedScenario.remaining_savings.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.default'
        }
      }}
    >
      <DialogTitle sx={{ bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyOff color="primary" />
          <Typography variant="h6" sx={{ color: 'text.primary' }}>
            Account Offset - {memberDetails?.name}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Member ID: {memberDetails?.member_id}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: 'background.default' }}>
        {renderPreview()}
      </DialogContent>

      <DialogActions sx={{ bgcolor: 'background.paper' }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleExecuteOffset}
          variant="contained"
          color="error"
          disabled={loading || !preview || error}
          startIcon={loading ? <CircularProgress size={20} /> : <TrendingDown />}
        >
          {loading ? 'Processing...' : 'Confirm Offset'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountOffsetDialog;